import { ROLES } from "../constants";

const AUTHENTICATED_ROLES = new Set(Object.values(ROLES));
const SCAN_IN_ROLES = new Set([
  ROLES.INSCANNER,
  ROLES.INSCANNER_PLUS,
  ROLES.SUPERVISOR,
  ROLES.ADMIN,
]);
const STOCK_MANAGEMENT_ROLES = new Set([
  ROLES.PLANNING,
  ROLES.ADMIN,
]);
const PLANNING_MANAGEMENT_ROLES = new Set([
  ROLES.PLANNING,
  ROLES.SUPERVISOR,
  ROLES.ADMIN,
]);

export const APP_PATHS = Object.freeze({
  HOME: "/",
  INVENTORY: "/voorraad-beheer",
  HISTORY: "/historie",
  ORDER_DATA: "/Order-data",
  WEEK_ORDERS: "/weekorders",
  PALLET_STOCK: "/palletvoorraad",
  PALLET_ASSIGNMENT: "/voorraad-koppelen",
  FUST_STOCK: "/fustvoorraad",
  PLANNING: "/planning",
  PARCELS: "/Percelen-beheer",
  WOODEN_LABELS: "/houten-labels",
  ALLOWED_CRATES: "/kisten-beheer",
  CRATE_LOOKUP: "/kist-opzoeken",
  USERS: "/users",
});

const normalizePath = (pathname) => {
  const normalized = String(pathname || APP_PATHS.HOME)
    .trim()
    .toLowerCase();
  if (!normalized || normalized === APP_PATHS.HOME) return APP_PATHS.HOME;
  return normalized.replace(/\/+$/, "");
};

const isAuthenticatedRole = (role) => AUTHENTICATED_ROLES.has(role);

export function canRoleUsePhoneInscanner(role) {
  return role === ROLES.SUPERVISOR || role === ROLES.ADMIN;
}

export function canRoleAccessPath(role, pathname) {
  if (!isAuthenticatedRole(role)) return false;

  switch (normalizePath(pathname)) {
    case normalizePath(APP_PATHS.HOME):
    case normalizePath(APP_PATHS.HISTORY):
      return true;
    case normalizePath(APP_PATHS.INVENTORY):
    case normalizePath(APP_PATHS.WEEK_ORDERS):
    case normalizePath(APP_PATHS.PALLET_STOCK):
    case normalizePath(APP_PATHS.FUST_STOCK):
      return STOCK_MANAGEMENT_ROLES.has(role);
    case normalizePath(APP_PATHS.USERS):
      return role === ROLES.ADMIN;
    case normalizePath(APP_PATHS.ORDER_DATA):
    case normalizePath(APP_PATHS.PLANNING):
    case normalizePath(APP_PATHS.PARCELS):
      return PLANNING_MANAGEMENT_ROLES.has(role);
    case normalizePath(APP_PATHS.PALLET_ASSIGNMENT):
      return role === ROLES.UITSCANNER || role === ROLES.ADMIN;
    case normalizePath(APP_PATHS.WOODEN_LABELS):
      return PLANNING_MANAGEMENT_ROLES.has(role);
    case normalizePath(APP_PATHS.CRATE_LOOKUP):
      return [ROLES.SUPERVISOR, ROLES.PLANNING, ROLES.ADMIN].includes(role);
    case normalizePath(APP_PATHS.ALLOWED_CRATES):
      return role === ROLES.ADMIN;
    default:
      return false;
  }
}

export function getRoleDataNeeds(role, pathname) {
  const path = normalizePath(pathname);
  const authenticated = isAuthenticatedRole(role);
  const onCrateLookup = path === normalizePath(APP_PATHS.CRATE_LOOKUP);
  const onPhoneScanner = onCrateLookup && canRoleUsePhoneInscanner(role);
  const onHome =
    path === normalizePath(APP_PATHS.HOME) ||
    path === normalizePath(APP_PATHS.INVENTORY) ||
    onPhoneScanner;
  const onHistory = path === normalizePath(APP_PATHS.HISTORY);
  const onPlanning = path === normalizePath(APP_PATHS.PLANNING);
  const canScanIn = SCAN_IN_ROLES.has(role);
  const managesPlanning = PLANNING_MANAGEMENT_ROLES.has(role);

  return Object.freeze({
    authenticated,
    coolingRows:
      authenticated &&
      (onHome || onPlanning),
    inventory:
      authenticated &&
      (canScanIn || managesPlanning) &&
      (onHome || onHistory),
    planningQueues:
      authenticated &&
      role !== ROLES.UITSCANNER &&
      (canScanIn || managesPlanning) &&
      (onHome || onPlanning),
    partySession: authenticated && canScanIn && onHome,
    parcelOptions:
      authenticated && role !== ROLES.UITSCANNER && onHome,
    coolingCellOptions:
      authenticated && role !== ROLES.UITSCANNER && onHome,
    machines:
      authenticated &&
      ((role === ROLES.PLANNING && !onCrateLookup) ||
        (role === ROLES.UITSCANNER && path === normalizePath(APP_PATHS.HOME))),
    fustAlerts:
      authenticated && STOCK_MANAGEMENT_ROLES.has(role),
  });
}

export function getLegacyRoleDataNeeds(role) {
  const authenticated = isAuthenticatedRole(role);
  const canScanIn = SCAN_IN_ROLES.has(role);
  const managesPlanning = PLANNING_MANAGEMENT_ROLES.has(role);

  return Object.freeze({
    authenticated,
    coolingRows: authenticated,
    inventory: authenticated && (canScanIn || managesPlanning),
    planningQueues: authenticated,
    partySession: authenticated,
    parcelOptions: authenticated,
    coolingCellOptions: authenticated,
    machines:
      authenticated &&
      (role === ROLES.PLANNING || role === ROLES.UITSCANNER),
    fustAlerts:
      authenticated && STOCK_MANAGEMENT_ROLES.has(role),
  });
}

export const roleAccessTestables = {
  normalizePath,
};
